// Pest content library. Editorial, homeowner-first. No invented statistics,
// certifications, or pesticide instructions — guidance defers to product labels
// and licensed professionals where specifics are required.

export const PESTS = [
  {
    slug: "mosquitoes",
    name: "Mosquitoes",
    short: "The backyard's most persistent party crasher.",
    tagline: "Dealing with mosquitoes?",
    icon: "Droplets",
    accent: "#2B7A78",
    diyFeasibility: "Often DIY-friendly",
    feasibilityLevel: 2, // 1 easy, 2 moderate, 3 call pro
    urgency: "Comfort & nuisance",
    heroImage:
      "https://images.unsplash.com/photo-1704544704633-d5c644c5cb68?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    cardImage:
      "https://images.unsplash.com/photo-1704544704633-d5c644c5cb68?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    intro:
      "Most backyard mosquito problems come down to two things: standing water where they breed, and shady, humid spots where they rest during the day. The good news is that a lot of what makes a yard mosquito-friendly is within a homeowner's control — before you spend a dollar on treatment.",
    identify: [
      "You notice the most biting around dawn and dusk, or whenever you sit still outside.",
      "Bites concentrate around ankles and any exposed skin during yard time.",
      "You spot slow, tumbling flyers near shady shrubs, tall grass, or damp corners.",
      "The problem gets dramatically worse a few days after rain.",
    ],
    causes: [
      { title: "Standing water", body: "Even a bottle cap of water can support larvae. Clogged gutters, saucers under planters, tarps, toys, and low spots that hold puddles are the usual suspects." },
      { title: "Dense, shady vegetation", body: "Overgrown shrubs and tall grass give adult mosquitoes a cool, humid place to rest out of the sun." },
      { title: "Poor drainage", body: "Areas that stay soggy for days after watering or rain quietly become breeding grounds." },
      { title: "Nearby sources you don't own", body: "A neighbor's untended pool, a drainage ditch, or a retention pond can keep re-seeding your yard." },
    ],
    diySteps: [
      { title: "Dump the standing water", body: "Walk the whole yard once a week and empty anything holding water — saucers, buckets, tarps, toys, wheelbarrows, and clogged gutters. This is the single highest-impact thing most homeowners can do." },
      { title: "Fix the slow drains", body: "Level low spots that puddle, and keep gutters and downspouts flowing so water doesn't pool." },
      { title: "Cut back the shade", body: "Trim dense shrubs and keep grass mowed to reduce the humid daytime resting spots adults love." },
      { title: "Move the air", body: "Mosquitoes are weak flyers. An outdoor fan on a patio or seating area makes it hard for them to land on you." },
      { title: "Treat water you can't dump", body: "For rain barrels or ornamental water you want to keep, larvae-control products exist — follow the product label exactly for dosing and any pet/wildlife guidance." },
      { title: "Protect yourself during peak hours", body: "Long sleeves and an EPA-registered repellent used per its label go a long way at dawn and dusk." },
    ],
    products: ["outdoor-fan", "larvicide-dunks", "repellent", "gutter-guard"],
    whenToCallPro: [
      "You've dumped the water, trimmed the yard, and are still overrun week after week.",
      "You have a large property where walking and treating every source yourself isn't realistic.",
      "The source appears to be off your property (a ditch, pond, or neighbor's yard) and needs a broader plan.",
      "You want reliable relief for a specific event, or ongoing seasonal coverage of a big yard.",
    ],
    proTreatment: [
      "A walkthrough to identify breeding and resting areas specific to your property.",
      "Barrier applications to vegetation where adults rest, and/or larval treatments to water sources.",
      "A recurring schedule during mosquito season, since applications break down over time.",
      "Clear guidance on what products are used and any re-entry instructions for people and pets.",
    ],
    kidsPets:
      "Ask any pro exactly what product they're applying, where, and how long before kids and pets should stay off treated areas — the re-entry time comes from the product label. Consider moving pet bowls, toys, and play equipment before service, and ask about lower-exposure or larval-focused options if you have concerns. For DIY, most physical measures (dumping water, fans, screens) create zero chemical exposure at all.",
    faqs: [
      { q: "How much should mosquito control cost?", a: "Pricing varies a lot by yard size, region, and whether it's a one-time visit or a seasonal plan. Rather than chase a single number, ask providers to break down what's included per visit, how many visits a season, and whether it's a contract or pay-as-you-go. Our guides walk through the questions that make quotes comparable." },
      { q: "Why do I still have mosquitoes after treatment?", a: "The most common reasons are an untreated breeding source (often standing water you or a neighbor missed), rain that reintroduced larvae, or the natural break-down of a barrier application over time. If it keeps happening, ask your provider to re-inspect for a source rather than just re-spraying." },
      { q: "How often should a company treat?", a: "Barrier applications don't last forever, so most seasonal programs revisit on a regular cadence through mosquito season. Ask any provider to explain their interval and why — and what happens if mosquitoes return between visits." },
      { q: "Is DIY mosquito control worth it?", a: "For a lot of yards, yes — especially the free steps. Eliminating standing water, improving drainage, adding a fan to seating areas, and using repellent during peak hours solve many nuisance-level problems without professional treatment." },
    ],
    relatedGuides: ["mosquito-control-cost", "diy-mosquito-control", "still-have-mosquitoes", "how-often-mosquito-treat"],
  },
  {
    slug: "ticks",
    name: "Ticks",
    short: "Small, quiet, and worth taking seriously.",
    tagline: "Worried about ticks?",
    icon: "Bug",
    accent: "#8B5E34",
    diyFeasibility: "Prevention-first",
    feasibilityLevel: 2,
    urgency: "Health-relevant",
    heroImage:
      "https://images.unsplash.com/photo-1598788682896-012ceacdeabc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    cardImage:
      "https://images.unsplash.com/photo-1598788682896-012ceacdeabc?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    intro:
      "Ticks are less about swatting and more about avoiding contact in the first place. They wait in tall grass, leaf litter, and the shady edge where your lawn meets the woods. Most tick strategy is about making your yard less inviting and building simple habits for checking people and pets.",
    identify: [
      "You find them after time in tall grass, brush, or wooded edges — not usually in the open lawn middle.",
      "Pets that roam the yard edge come back with them attached.",
      "They're most active in the warmer months, but can appear whenever it's mild.",
      "You live near woods, stone walls, or areas where deer and small mammals travel.",
    ],
    causes: [
      { title: "The 'tick zone' at your yard's edge", body: "Ticks concentrate where mowed lawn meets tall grass, brush, or woods — the transition zone is prime habitat." },
      { title: "Leaf litter and ground cover", body: "Damp leaf litter, ivy, and dense ground cover hold the humidity ticks need." },
      { title: "Wildlife traffic", body: "Deer, mice, and other small mammals carry ticks into and across a property." },
      { title: "Overgrown, shaded borders", body: "Tall grass and unmanaged edges give ticks somewhere to wait for a host to brush past." },
    ],
    diySteps: [
      { title: "Create a barrier at the edge", body: "A strip of wood chips or gravel between lawn and woods creates a dry zone ticks are reluctant to cross." },
      { title: "Keep grass short and clear litter", body: "Mow regularly and rake up leaf litter, especially along fences, walls, and the woodland edge." },
      { title: "Move play areas into the sun", body: "Put swing sets, patios, and seating away from the yard edge and toward open, sunny spots." },
      { title: "Build a tick-check habit", body: "After yard or trail time, check people and pets — especially warm, hidden areas. Prompt, careful removal matters." },
      { title: "Protect pets deliberately", body: "Talk to your veterinarian about tick prevention for pets; the yard edge is where they pick most of them up." },
      { title: "Dress for the edge", body: "Long pants tucked into socks and an EPA-registered repellent (used per its label) reduce contact when you're near brush." },
    ],
    products: ["repellent", "tick-tubes", "yard-barrier", "pet-tick-prevention"],
    whenToCallPro: [
      "You back onto woods or fields and consistently find ticks despite yard maintenance.",
      "Someone in the household has a health concern that makes tick contact higher-stakes.",
      "You want a targeted perimeter treatment of the yard edge and transition zones.",
      "You've tried the environmental steps and still find ticks on people or pets regularly.",
    ],
    proTreatment: [
      "An inspection focused on the yard's edges, borders, and wildlife corridors.",
      "Targeted applications to the transition zones where ticks actually wait, rather than the whole open lawn.",
      "Guidance on timing treatments to the local tick season.",
      "Clear information on the product used and safe re-entry times for family and pets.",
    ],
    kidsPets:
      "Because ticks are a health-relevant pest, families understandably weigh treatment carefully. Perimeter treatments are usually targeted to yard edges rather than play areas — ask any pro exactly where they'll treat and the label's re-entry time before kids and pets return. Non-chemical steps (edge barriers, mowing, moving play areas to the sun, and diligent tick checks) do a lot of the work and add no exposure. Always coordinate pet tick prevention with your veterinarian.",
    faqs: [
      { q: "Are tick treatments worth it?", a: "For properties that border woods or fields and see ticks regularly, a targeted perimeter treatment can meaningfully reduce contact when combined with yard maintenance. For yards with little tick pressure, environmental steps and good tick-check habits may be enough. It depends on your property and risk tolerance." },
      { q: "Where do ticks actually live in my yard?", a: "Mostly at the edges — where lawn meets tall grass, brush, stone walls, or woods, and in damp leaf litter. The open middle of a sunny, mowed lawn is much less hospitable, which is why moving play areas toward the sun helps." },
      { q: "What should I do if I find a tick attached?", a: "Careful, prompt removal matters. For guidance on removal technique and any follow-up, follow reputable public-health instructions and consult a medical professional with health questions — that's outside what a pest resource should advise." },
      { q: "Can I protect my pets from yard ticks?", a: "Yard steps help, but pets need their own prevention. Talk to your veterinarian about the right tick prevention product for your animal — the yard edge is exactly where they tend to pick ticks up." },
    ],
    relatedGuides: ["tick-treatments-worth-it", "things-to-check-before-hiring"],
  },
  {
    slug: "wasps-hornets",
    name: "Wasps & Hornets",
    short: "Usually a location problem more than a bug problem.",
    tagline: "Wasps or hornets nesting nearby?",
    icon: "Zap",
    accent: "#D97736",
    diyFeasibility: "Depends on the nest",
    feasibilityLevel: 3,
    urgency: "Can be time-sensitive",
    heroImage:
      "https://images.unsplash.com/photo-1651654603894-74759c570a64?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    cardImage:
      "https://images.unsplash.com/photo-1651654603894-74759c570a64?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    intro:
      "A wasp or hornet issue usually comes down to where the nest is. A small, early nest in an open, out-of-the-way spot is a different situation than a large nest tucked into a wall void or right beside a doorway. The location and size decide whether this is a careful DIY job or a clear call-a-pro moment.",
    identify: [
      "You see steady traffic in and out of one specific spot — an eave, wall gap, shrub, or ground hole.",
      "A papery nest is visible under an eave, in a shrub, or in a shed.",
      "Activity is concentrated and defensive near the nest, not just occasional visitors.",
      "The nest is growing noticeably week over week.",
    ],
    causes: [
      { title: "Sheltered nesting spots", body: "Eaves, wall voids, sheds, playsets, and dense shrubs offer protected places to build." },
      { title: "Food and sugar sources", body: "Open trash, fallen fruit, pet food, and sweet drinks draw foraging wasps to an area." },
      { title: "Existing gaps and voids", body: "Openings into wall or roof voids let a nest set up somewhere you can't easily reach." },
    ],
    diySteps: [
      { title: "Confirm size and location first", body: "A small, new, exposed nest in a low-traffic area is the only scenario most homeowners should consider handling — and only if no one in the household reacts to stings." },
      { title: "Never seal an active nest into a void", body: "Sealing a wall opening with an active nest inside can push wasps into your living space. Don't." },
      { title: "Remove the attractants", body: "Keep trash lids closed, clean up fallen fruit and spills, and cover sweet drinks outdoors." },
      { title: "Deal with it at rest", body: "If you do treat a small exposed nest yourself, do it when activity is lowest and follow the product label precisely — including protective clothing and distance." },
      { title: "Have an exit plan", body: "Never work off a ladder near an active nest. If it's high, near a door, or in a void, that's a pro job." },
    ],
    products: ["wasp-spray", "protective-clothing"],
    whenToCallPro: [
      "The nest is large, inside a wall or roof void, high up, or beside a door or walkway.",
      "Anyone in the household has a known sting allergy or sensitivity.",
      "It's a hornet nest, or a species you can't confidently identify.",
      "You've disturbed it once already, or it keeps rebuilding after removal.",
    ],
    proTreatment: [
      "Identification of the species and the full extent of the nest.",
      "Treatment and removal using proper protective equipment and technique.",
      "Sealing or exclusion of the entry point after the nest is confirmed inactive.",
      "Guidance on keeping the area clear and any re-entry instructions.",
    ],
    kidsPets:
      "Sting risk is the priority here, especially with kids and pets around doorways and play areas. If a nest is anywhere near where your family spends time, keeping everyone clear and calling a pro is usually the sensible call. Ask about the treatment used and when the area is safe to use again.",
    faqs: [
      { q: "Can I remove a wasp nest myself?", a: "Only if it's small, new, exposed, low, in a low-traffic spot, and no one in your household reacts to stings — and even then, carefully and per the product label. Anything large, high, in a wall or roof void, or near a doorway is a pro job." },
      { q: "Why do wasps keep coming back to the same spot?", a: "Either there's still an attractant nearby (trash, fallen fruit, sweet drinks) or a favorable nesting void that keeps drawing them. Removing the nest without addressing the location often leads to a rebuild." },
    ],
    relatedGuides: ["things-to-check-before-hiring"],
  },
  {
    slug: "ants",
    name: "Ants",
    short: "Follow the trail before you reach for the spray.",
    tagline: "Ants marching through your kitchen?",
    icon: "Route",
    accent: "#6B7A4F",
    diyFeasibility: "Usually DIY-friendly",
    feasibilityLevel: 1,
    urgency: "Nuisance (mostly)",
    heroImage:
      "https://images.unsplash.com/photo-1603474549458-606316a15847?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    cardImage:
      "https://images.unsplash.com/photo-1603474549458-606316a15847?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    intro:
      "Most kitchen ant problems are a food-and-moisture problem. The ants you see are scouts following a trail to something — crumbs, spills, sticky jars, pet food. Break the trail, remove the food, and seal the entry points, and a lot of ant issues resolve without a treatment plan at all.",
    identify: [
      "You see steady lines of ants following a consistent path to a food source.",
      "Activity concentrates around the kitchen, pantry, sink, or pet bowls.",
      "Trails appear along baseboards, counters, or window edges.",
      "It gets worse in warm or dry stretches when they come inside for food and water.",
    ],
    causes: [
      { title: "Accessible food", body: "Crumbs, spills, sticky containers, and open pet food are the usual draw." },
      { title: "Moisture", body: "Leaky pipes, damp cabinets under the sink, and standing water attract several ant types." },
      { title: "Easy entry points", body: "Gaps around windows, doors, pipes, and foundation cracks are the highways in." },
    ],
    diySteps: [
      { title: "Cut off the food", body: "Wipe up crumbs and spills, store food in sealed containers, and don't leave pet food out overnight." },
      { title: "Break the scent trail", body: "Wipe down the trails you see with soapy water to disrupt the chemical path scouts leave behind." },
      { title: "Seal the entry points", body: "Caulk gaps around windows, doors, and where pipes enter — this keeps the next wave out." },
      { title: "Fix the moisture", body: "Repair drips under the sink and dry out damp cabinet areas." },
      { title: "Use baits thoughtfully", body: "Baits let workers carry the product back to the colony. If you use them, follow the label and place them out of reach of kids and pets." },
    ],
    products: ["ant-bait", "caulk-sealant"],
    whenToCallPro: [
      "The ants are carpenter ants damaging wood, not just foraging for food.",
      "You have large, repeated indoor trails you can't trace to a source.",
      "Baiting and sanitation haven't worked after a genuine effort.",
      "You suspect a structural nest inside walls or the foundation.",
    ],
    proTreatment: [
      "Identifying the ant species, since strategy differs (especially for carpenter ants).",
      "Locating the nest and entry points rather than only treating visible trails.",
      "A targeted treatment plan matched to the species and situation.",
      "Guidance on sanitation and exclusion so it doesn't come back.",
    ],
    kidsPets:
      "Baits and any indoor products should be placed where children and pets can't reach them — follow the label placement guidance. Much of the ant playbook (sanitation, sealing gaps, fixing leaks) involves no chemicals at all, which makes it a great DIY-first pest for households with kids and pets.",
    faqs: [
      { q: "Should I spray the ants I see?", a: "Spraying visible ants can actually make some colonies harder to control by scattering them. Sanitation, sealing entry points, and using baits per the label to reach the colony is usually more effective than surface spraying." },
      { q: "When are ants a bigger problem?", a: "Carpenter ants that tunnel into wood are a structural concern, not just a nuisance. If you see large ants and sawdust-like debris, that's a good moment to bring in a pro." },
    ],
    relatedGuides: ["things-to-check-before-hiring"],
  },
  {
    slug: "fleas",
    name: "Fleas",
    short: "Usually a pet-and-home problem together.",
    tagline: "Fighting fleas at home?",
    icon: "PawPrint",
    accent: "#A8577E",
    diyFeasibility: "DIY-possible, pet vet essential",
    feasibilityLevel: 2,
    urgency: "Comfort & pets",
    heroImage:
      "https://images.unsplash.com/photo-1773013735299-5c740f20de55?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    cardImage:
      "https://images.unsplash.com/photo-1773013735299-5c740f20de55?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    intro:
      "Fleas live on a cycle that spans your pet, your carpets, and your yard at the same time — which is why treating just one rarely works. The two anchors are veterinary flea control for your animals and thorough, repeated cleaning of the home. Skip either and the cycle tends to restart.",
    identify: [
      "Pets scratch, bite, or groom more than usual.",
      "You spot tiny fast-moving specks, or 'flea dirt' in pet bedding and fur.",
      "You get small bites, often around the ankles.",
      "It flares in warm, humid conditions.",
    ],
    causes: [
      { title: "Untreated pets", body: "Pets are the primary host; without vet-recommended prevention the cycle keeps going." },
      { title: "Eggs and larvae in the home", body: "Carpets, rugs, bedding, and cracks hold eggs and larvae, not just adult fleas." },
      { title: "Shady yard spots", body: "Fleas can persist in cool, shaded areas of the yard where pets rest." },
      { title: "Wildlife visitors", body: "Strays and wildlife passing through can reintroduce fleas to the yard." },
    ],
    diySteps: [
      { title: "Start with the vet", body: "Effective, safe flea control for your specific pet comes from your veterinarian — this is the anchor of the whole plan." },
      { title: "Vacuum relentlessly", body: "Vacuum carpets, rugs, furniture, and cracks frequently, and dispose of the contents so eggs and larvae don't survive indoors." },
      { title: "Wash the soft stuff hot", body: "Launder pet bedding and any washable soft goods on hot regularly during an active infestation." },
      { title: "Address shaded yard areas", body: "Focus on the cool, shady spots pets frequent; clear debris and let sun in where you can." },
      { title: "Follow labels on any home product", body: "If you use a home flea product, follow the label precisely — including keeping pets and kids off treated areas for the stated time." },
    ],
    products: ["pet-tick-prevention", "flea-vacuum"],
    whenToCallPro: [
      "The infestation persists after weeks of vet treatment plus thorough cleaning.",
      "It's widespread across multiple rooms and the yard at once.",
      "You're in a rental or large home where DIY cleaning can't keep up.",
      "You want the home and yard treated in coordination with your vet's plan.",
    ],
    proTreatment: [
      "A plan that treats the home environment in step with veterinary care for pets.",
      "Targeting of eggs and larvae in carpets and cracks, not just adult fleas.",
      "Attention to shaded yard areas where fleas persist.",
      "Clear label-based re-entry times for pets and family after any application.",
    ],
    kidsPets:
      "Fleas are inherently a kids-and-pets situation, so coordination matters. The pet side belongs with your veterinarian — never apply a product meant for one species to another, and never combine products without professional guidance. For the home, most of the heavy lifting (vacuuming, hot washing) is chemical-free. If a home or yard product is used, follow the label's re-entry time for children and pets exactly.",
    faqs: [
      { q: "Why do fleas keep coming back?", a: "Almost always because one part of the cycle went untreated — usually eggs and larvae in the home, or a pet without effective vet-provided prevention. Treating pet, home, and (if needed) yard together, and repeating over a few weeks, is what breaks it." },
      { q: "Can I treat fleas without involving my vet?", a: "The pet part should always go through your veterinarian for a product that's safe and effective for your specific animal. Home cleaning you can absolutely do yourself alongside that." },
    ],
    relatedGuides: ["things-to-check-before-hiring"],
  },
  {
    slug: "rodents",
    name: "Rodents",
    short: "Seal it out, then deal with what's inside.",
    tagline: "Hearing something in the walls?",
    icon: "Home",
    accent: "#7A6A55",
    diyFeasibility: "Minor: DIY. Active: call a pro",
    feasibilityLevel: 3,
    urgency: "Property & health-relevant",
    heroImage:
      "https://images.unsplash.com/photo-1624116518496-993146f67f4a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    cardImage:
      "https://images.unsplash.com/photo-1624116518496-993146f67f4a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    intro:
      "Rodent control is really two jobs: keeping them out (exclusion) and dealing with any already inside. Exclusion — finding and sealing the gaps they use — is the part that actually prevents a repeat. It's also where a lot of DIY efforts fall short, because rodents exploit surprisingly small openings.",
    identify: [
      "You hear scratching or scurrying in walls, ceilings, or the attic, often at night.",
      "You find droppings along walls, in cabinets, or in the garage.",
      "You see gnaw marks on food packaging, wood, or wiring.",
      "Pets fixate on a spot in the wall or floor.",
    ],
    causes: [
      { title: "Entry points", body: "Gaps around pipes, vents, rooflines, and the foundation — some no bigger than a coin — are all it takes." },
      { title: "Food access", body: "Pet food, birdseed, pantry items, and trash give them a reason to stay." },
      { title: "Shelter and clutter", body: "Cluttered garages, sheds, and storage areas offer nesting sites." },
      { title: "Exterior harborage", body: "Woodpiles, dense vegetation against the house, and overgrowth near the foundation invite them close." },
    ],
    diySteps: [
      { title: "Find and seal the gaps", body: "Inspect where pipes and wires enter, along the roofline, and around the foundation. Seal openings with appropriate, gnaw-resistant materials." },
      { title: "Cut off the food", body: "Store pantry items and pet food in sealed containers, secure trash, and clean up spills promptly." },
      { title: "Clear the harborage", body: "Move woodpiles away from the house, trim vegetation off the foundation, and declutter garages and sheds." },
      { title: "Know DIY's limit", body: "A single mouse caught early can be a DIY moment. Ongoing activity, droppings in multiple areas, or noises in the walls means the exclusion and removal job is bigger than most homeowners should tackle alone." },
    ],
    products: ["steel-wool-sealant", "snap-traps"],
    whenToCallPro: [
      "You hear ongoing activity in walls, ceilings, or the attic.",
      "You find droppings in multiple areas or evidence of gnawed wiring.",
      "DIY sealing and trapping haven't stopped it.",
      "You want a thorough exclusion job so it doesn't recur.",
    ],
    proTreatment: [
      "A full inspection to map entry points and activity.",
      "Professional exclusion — sealing the structure properly so rodents can't return.",
      "A removal plan for animals already inside.",
      "Guidance on sanitation and any health-relevant cleanup, plus safe placement away from kids and pets.",
    ],
    kidsPets:
      "Rodents raise real hygiene concerns, and any control products (including traps and baits) must be placed well out of reach of children and pets — follow all label and placement guidance. Because rodent droppings can be a health matter, cleanup should follow reputable public-health guidance; when in doubt, have a professional handle it.",
    faqs: [
      { q: "Do I really need a pro for a rodent problem?", a: "For a single mouse caught early with an obvious entry point you can seal, maybe not. But ongoing noises in the walls, droppings in multiple areas, or gnawed wiring point to a bigger exclusion-and-removal job where a pro's inspection pays off." },
      { q: "What's the most important step?", a: "Exclusion — actually finding and sealing every entry point. Trapping the animals inside without sealing the gaps just invites the next ones in." },
    ],
    relatedGuides: ["things-to-check-before-hiring"],
  },
];

export const getPest = (slug) => PESTS.find((p) => p.slug === slug);

// Loosely resolve free text ("Mosquito", "mosquito control") to a pest slug.
// Used by location pages, where the pest name is derived from a URL segment.
export const matchPestSlug = (text) => {
  if (!text) return null;
  const t = String(text).toLowerCase().replace(/[^a-z]+/g, "");
  const hit = PESTS.find((p) => {
    const name = p.name.toLowerCase().replace(/[^a-z]+/g, "");
    const slug = p.slug.replace(/[^a-z]+/g, "");
    return name.startsWith(t) || t.startsWith(name) || slug.startsWith(t) || t.startsWith(slug);
  });
  return hit ? hit.slug : null;
};
